if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initGalleryNavigation();
        this.setupImageLoadStates();
        this.preloadInitialSlides();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      getSlides() {
        if (!this.elements.viewer) return [];
        return Array.from(
          this.elements.viewer.querySelectorAll('.product__media-item.slider__slide[data-media-id]')
        ).filter((slide) => !slide.classList.contains('is-color-hidden'));
      }

      setupImageLoadStates() {
        this.querySelectorAll('.product-gallery__image').forEach((image) => {
          if (image.complete) {
            image.classList.add('is-loaded');
            return;
          }

          image.addEventListener('load', () => image.classList.add('is-loaded'), { once: true });
          image.addEventListener('error', () => image.classList.add('is-loaded'), { once: true });
        });
      }

      preloadSlideImages(slide) {
        if (!slide) return;

        slide.querySelectorAll('.product-gallery__image, .product__media img').forEach((image) => {
          if (image.loading === 'lazy') image.loading = 'eager';

          if (image.complete) {
            image.classList.add('is-loaded');
            return;
          }

          const preloadSrc = image.currentSrc || image.src;
          if (preloadSrc) {
            const loader = new Image();
            loader.decoding = 'async';
            loader.src = preloadSrc;
            loader.onload = () => image.classList.add('is-loaded');
            loader.onerror = () => image.classList.add('is-loaded');
          }
        });
      }

      preloadAdjacentSlides(mediaId) {
        const slides = this.getSlides();
        const activeIndex = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (activeIndex < 0) return;

        [activeIndex - 1, activeIndex + 1].forEach((index) => {
          if (index >= 0 && index < slides.length) this.preloadSlideImages(slides[index]);
        });
      }

      preloadInitialSlides() {
        const slides = this.getSlides().slice(0, 3);
        slides.forEach((slide) => this.preloadSlideImages(slide));
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.preloadAdjacentSlides(event.detail.currentElement.dataset.mediaId);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `.product__media-item[data-media-id="${mediaId}"]:not(.is-color-hidden)`
          ) ||
          this.elements.viewer.querySelector('.product__media-item[data-media-id]:not(.is-color-hidden)') ||
          this.elements.viewer.querySelector(`.product__media-item[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.product__media-item[data-media-id]');
        if (!activeMedia) {
          return;
        }

        this.preloadSlideImages(activeMedia);
        this.preloadAdjacentSlides(mediaId);

        this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        if (image.complete) {
          announce();
          return;
        }

        image.addEventListener('load', announce, { once: true });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initGalleryNavigation() {
        const viewer = this.elements.viewer;
        if (!viewer) return;

        const slides = () => this.getSlides();
        if (slides().length < 2) return;

        const nav = viewer.querySelector('.product__gallery-nav');
        const prevButton = nav?.querySelector('button[name="previous"]');
        const nextButton = nav?.querySelector('button[name="next"]');
        const mediaList = viewer.querySelector('.product__media-list');

        const keepNavButtonsEnabled = () => {
          [prevButton, nextButton].forEach((button) => button?.removeAttribute('disabled'));
        };

        keepNavButtonsEnabled();

        if (prevButton && nextButton) {
          const disabledObserver = new MutationObserver(keepNavButtonsEnabled);
          [prevButton, nextButton].forEach((button) => {
            disabledObserver.observe(button, { attributes: true, attributeFilter: ['disabled'] });
          });
        }

        const navigate = (direction) => {
          const allSlides = slides();
          let activeIndex = allSlides.findIndex((slide) => slide.classList.contains('is-active'));
          if (activeIndex < 0) activeIndex = 0;

          let nextIndex = activeIndex + direction;
          if (nextIndex < 0) nextIndex = allSlides.length - 1;
          if (nextIndex >= allSlides.length) nextIndex = 0;

          const mediaId = allSlides[nextIndex].dataset.mediaId;

          if (this.elements.thumbnails) {
            this.setActiveMedia(mediaId, false);
            return;
          }

          const targetSlide = allSlides[nextIndex];
          allSlides.forEach((slide) => slide.classList.toggle('is-active', slide === targetSlide));
          this.preloadSlideImages(targetSlide);
          this.preloadAdjacentSlides(mediaId);
          if (typeof viewer.setSlidePosition === 'function' && targetSlide) {
            viewer.setSlidePosition(targetSlide.offsetLeft);
          }
        };

        nav?.addEventListener(
          'click',
          (event) => {
            const button = event.target.closest('button[name="previous"], button[name="next"]');
            if (!button) return;

            event.preventDefault();
            event.stopImmediatePropagation();
            navigate(button.name === 'next' ? 1 : -1);
          },
          true
        );

        if (!mediaList) return;

        let startX = 0;
        let isDragging = false;
        const dragThreshold = 50;

        const onPointerDown = (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          if (
            event.target.closest(
              'button, .deferred-media__poster, model-viewer, .image-magnify-hover, .product-gallery__image, .product__media-zoom-inline'
            )
          ) {
            return;
          }
          if (!this.mql.matches && event.pointerType === 'mouse') return;

          startX = event.clientX;
          isDragging = true;
          mediaList.classList.add('is-dragging');
          mediaList.setPointerCapture(event.pointerId);
        };

        const onPointerUp = (event) => {
          if (!isDragging) return;

          isDragging = false;
          mediaList.classList.remove('is-dragging');
          if (mediaList.hasPointerCapture(event.pointerId)) {
            mediaList.releasePointerCapture(event.pointerId);
          }

          const delta = event.clientX - startX;
          if (Math.abs(delta) < dragThreshold) return;

          navigate(delta < 0 ? 1 : -1);
        };

        const onPointerCancel = (event) => {
          isDragging = false;
          mediaList.classList.remove('is-dragging');
          if (mediaList.hasPointerCapture(event.pointerId)) {
            mediaList.releasePointerCapture(event.pointerId);
          }
        };

        mediaList.addEventListener('pointerdown', onPointerDown);
        mediaList.addEventListener('pointerup', onPointerUp);
        mediaList.addEventListener('pointercancel', onPointerCancel);
      }
    }
  );
}
s